import { AllocationRuleChooseInvestingEntityData } from "gen/model/allocationRuleChooseInvestingEntityData";
import { AllocationRuleInvestingEntitiesData } from "gen/model/allocationRuleInvestingEntitiesData";
import { GenericTableData } from "gen/model/genericTableData";
import { GenericTableRow } from "gen/model/genericTableRow";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "shared/components/table/data/DataTable";
import {
  OnChangeFn,
  RowSelectionState,
} from "shared/components/table/data/DataTableTypes";

export const AllocationSetupEntitiesTable: React.FC<{
  fundFamilyId: string;
  entityAllocationsData: AllocationRuleChooseInvestingEntityData[];
  currentInvestingEntityData: AllocationRuleInvestingEntitiesData;
  rowSelection: RowSelectionState;
  onRowSelectionChanged: OnChangeFn<RowSelectionState>;
  isEditable?: boolean;
}> = ({
  entityAllocationsData,
  currentInvestingEntityData,
  rowSelection,
  onRowSelectionChanged,
  isEditable = true,
}) => {
  const { t } = useTranslation(["allocation", "entity", "common", "enums"]);

  const tableData = useMemo<GenericTableData>(() => {
    const ruleIdForEntityIdMap = new Map(
      currentInvestingEntityData.entities.map((entity) => [
        entity.entity_id,
        entity.rule_id,
      ]),
    );

    return {
      columns: [
        {
          id: "g1",
          columns: [
            {
              id: "entity",
              title: t("allocation:setup.entities.entities.entity"),
              is_header: true,
            },
            {
              id: "rule",
              title: t("allocation:setup.entities.entities.sourceRule"),
            },
          ],
        },
        {
          id: "g2",
          columns: [
            {
              id: "country",
              title: t("allocation:setup.entities.entities.country"),
            },
            {
              id: "legalType",
              title: t("allocation:setup.entities.entities.legalType"),
            },
            {
              id: "functionalType",
              title: t("allocation:setup.entities.entities.functionalType"),
            },
          ],
        },
      ],
      rows: entityAllocationsData.map(
        (allocation): GenericTableRow => ({
          id: allocation.entity_id,
          granularity_level: "entity",
          cells: {
            entity: {
              value: {
                type: "text",
                text: allocation.entity_name,
              },
            },
            rule: isEditable
              ? {
                  value: {
                    type: "text",
                    text: ruleIdForEntityIdMap.get(allocation.entity_id) ?? "",
                  },
                  form_field: {
                    name: `entities.${allocation.entity_id}.rule_id`,
                    title: t("allocation:setup.entities.entities.sourceRule"),
                    options: allocation.rules,
                  },
                }
              : {
                  value: {
                    type: "text",
                    text:
                      allocation.rules.find(
                        (rule) =>
                          rule.value ===
                          ruleIdForEntityIdMap.get(allocation.entity_id),
                      )?.rich_value ?? "",
                  },
                },
            country: {
              value: {
                type: "text",
                text: allocation.country,
              },
            },
            legalType: {
              value: {
                type: "text",
                text: t(`enums:EntityLegalTypeEnum.${allocation.legal_type}`),
              },
            },
            functionalType: {
              value: {
                type: "text",
                text: t(
                  `enums:EntityFunctionalTypeEnum.${allocation.functional_type}`,
                ),
              },
            },
          },
        }),
      ),
    };
  }, [
    currentInvestingEntityData.entities,
    entityAllocationsData,
    isEditable,
    t,
  ]);

  return (
    <DataTable
      rowSelection={isEditable ? rowSelection : undefined}
      onRowSelectionChange={isEditable ? onRowSelectionChanged : undefined}
      data={tableData}
      emptyStateProps={{
        title: t("allocation:setup.entities.entities.empty"),
      }}
    />
  );
};
